import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const ProjectsPage: React.FC = () => {
  const categories = ["All", "Manufacturing", "Fabrication", "Installation"];
  const [activeCategory, setActiveCategory] = useState("All");
  const navigate = useNavigate();

  const projects = [
    {
      title: "Palm Threshing Machine",
      category: "Manufacturing",
      image: "/palm_threshing_machine.png",
      description:
        "High-efficiency palm fruit threshing machine designed for optimal fruit separation.",
      details: [
        "Capacity: 2 tons per hour",
        "Power: Electric motor driven",
        "Material: Stainless steel construction",
        "Application: Palm fruit processing",
      ],
    },
    {
      title: "Palm Kernel Oil Machine",
      category: "Manufacturing",
      image: "/palm_kenel_oil_machines.png",
      description:
        "Advanced palm kernel oil extraction machine with high yield rate.",
      details: [
        "Output: 500L per day",
        "Automated process control",
        "Energy-efficient design",
        "Integrated filtering system",
      ],
    },
    {
      title: "Industrial Drying Machine",
      category: "Manufacturing",
      image: "/drying_machine.png",
      description:
        "Industrial-grade drying machine for agricultural products processing.",
      details: [
        "Temperature control system",
        "Multiple drying chambers",
        "Energy recovery system",
        "Digital control interface",
      ],
    },
    {
      title: "Pasteurizer Unit",
      category: "Fabrication",
      image: "/pasturizer.png",
      description:
        "Custom fabricated pasteurization unit for food processing industry.",
      details: [
        "Temperature range: 60-95°C",
        "Capacity: 1000L per hour",
        "CIP cleaning system",
        "Digital temperature control",
      ],
    },
  ];

  const filteredProjects =
    activeCategory === "All"
      ? projects
      : projects.filter((project) => project.category === activeCategory);

  const handleProjectClick = (projectTitle: string) => {
    const slug = projectTitle.toLowerCase().replace(/\s+/g, "-");
    navigate(`/projects/${slug}`);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Our Projects"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Our Projects</h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Explore our portfolio of successful manufacturing and fabrication
            projects. Each project showcases our commitment to quality and
            innovation.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full transition-colors ${
                  activeCategory === category
                    ? "bg-orange-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden group cursor-pointer"
                onClick={() => handleProjectClick(project.title)}
              >
                <div className="relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity duration-300"></div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm">
                      {project.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  <div className="space-y-2">
                    {project.details.map((detail, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-gray-600"
                      >
                        <svg
                          className="w-4 h-4 text-orange-500 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Have a Project in Mind?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your manufacturing or
            fabrication project to life. Our team is ready to deliver
            excellence.
          </p>
          <button className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors">
            Start Your Project
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
